import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Panel } from '../../components/Overlays';

/** What a spool label's QR holds: a link that opens the spool (the phone camera opens it too). */
export const spoolLabelUrl = (code: string) =>
  `${window.location.origin}/c/${encodeURIComponent(code)}`;

/** "C-0012" from a scanned link or typed text, or null. */
export function codeFrom(text: string): string | null {
  const m = /(?:\/c\/)?(C-?\d{1,6})\s*$/i.exec(decodeURIComponent(text.trim()));
  if (!m) return null;
  const digits = m[1]!.replace(/[^0-9]/g, '');
  return `C-${digits.padStart(4, '0')}`;
}

export function QrImage({ text, size = 120, alt }: { text: string; size?: number; alt?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void QRCode.toDataURL(text, { margin: 1, width: size * 2, errorCorrectionLevel: 'M' }).then(
      (url) => {
        if (alive) setSrc(url);
      },
    );
    return () => {
      alive = false;
    };
  }, [text, size]);
  return src ? (
    <img
      src={src}
      alt={alt ?? `QR ${text}`}
      width={size}
      height={size}
      style={{ imageRendering: 'pixelated' }}
    />
  ) : (
    <span className="ph" style={{ width: size, height: size }} />
  );
}

interface Detector {
  detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]>;
}
type DetectorCtor = new (opts: { formats: string[] }) => Detector;

/**
 * "Ler etiqueta": camera scan with the browser's BarcodeDetector where available, otherwise
 * typing the code. Nothing leaves the device.
 */
export { Panel };

export function QrScanSheet({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const video = useRef<HTMLVideoElement>(null);
  const [typed, setTyped] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const Ctor: DetectorCtor | undefined =
    'BarcodeDetector' in globalThis ? Reflect.get(globalThis, 'BarcodeDetector') : undefined;
  const go = (code: string) => {
    onClose();
    void navigate(`/c/${code}`);
  };

  useEffect(() => {
    if (!Ctor || !navigator.mediaDevices?.getUserMedia) return;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;
    const detector = new Ctor({ formats: ['qr_code'] });
    void navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        if (stopped) {
          for (const t of s.getTracks()) t.stop();
          return;
        }
        stream = s;
        if (video.current) {
          video.current.srcObject = s;
          void video.current.play();
        }
        timer = window.setInterval(() => {
          if (!video.current || video.current.readyState < 2) return;
          void detector.detect(video.current).then((found) => {
            const code = found.map((f) => codeFrom(f.rawValue)).find((c) => c !== null);
            if (code) {
              stopped = true;
              window.clearInterval(timer);
              go(code);
            }
          });
        }, 350);
      })
      .catch(() => setProblem('Sem acesso à câmera. Digite o código da etiqueta.'));
    return () => {
      stopped = true;
      window.clearInterval(timer);
      for (const t of stream?.getTracks() ?? []) t.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per open sheet
  }, []);

  const code = codeFrom(typed);
  return (
    <Panel label="Ler etiqueta" onClose={onClose}>
      {Ctor && !problem ? (
        <>
          <video
            ref={video}
            muted
            playsInline
            style={{
              width: '100%',
              borderRadius: 12,
              background: '#1D1D1D',
              aspectRatio: '1/1',
              objectFit: 'cover',
            }}
          />
          <p className="sm muted">Aponte a câmera para a etiqueta do carretel.</p>
        </>
      ) : (
        <p className="sm muted">
          {problem ??
            'Este navegador não lê QR aqui. Use a câmera do celular na etiqueta, ou digite o código.'}
        </p>
      )}
      <div className="field">
        <label htmlFor="qr-code">Código do carretel</label>
        <input
          id="qr-code"
          className="input"
          placeholder="C-0012"
          autoCapitalize="characters"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code) go(code);
          }}
        />
      </div>
      <button
        type="button"
        className="btn p block"
        disabled={!code}
        onClick={() => code && go(code)}
      >
        Abrir carretel
      </button>
      <button type="button" className="btn block" onClick={onClose}>
        Cancelar
      </button>
    </Panel>
  );
}
